import React from 'react';
import { observer } from 'mobx-react-lite';
import { AiLogoSVGIcon, CloseSVGIcon } from '../svg';
import classNames from 'classnames';
import { DotSpinner } from '../loader';
import { ellipseText } from '../utils';
import { PlayerSearchBarState } from './playerSearchBarState';
import { TextInputControlled } from '../input';
import { CSSTransition } from 'react-transition-group';
import { useOutsideClick } from '../hooks';
import { DEFAULT_GPT_QUESTION } from '../../constants/ai';

type Props = {
  state: PlayerSearchBarState;
  className?: string | null;
};

const AiProcessingTag = () => (
  <div className="job-card-ai-tag processing">
    <AiLogoSVGIcon />
    <div className="spinner-container">
      Still processing
      <DotSpinner className="short-animation" dots={3} />
    </div>
  </div>
);

type SuggestionProps = {
  onClick: (evt: React.MouseEvent<Element, MouseEvent>) => void;
  text: string;
  disabled?: boolean | null;
  isProcessing?: boolean | null;
}

const Suggestion = ({ onClick, text, isProcessing, disabled }: SuggestionProps) => (
  <div 
    onClick={onClick} 
    aria-disabled={disabled ?? false} 
    className="suggestion">
    {text}
    {isProcessing && <AiProcessingTag />}
  </div>
);

export const AiPlayerSearchBar = observer(({ state, className, ...props }: Props) => {
  const {
    isWidgetMode,
    searchQuery,
    isAiProcessing,
    showIcon,
    dropdownOpened,
    dropdownElement,
    placeholder,
    handleClear,
    handleKeyUp,
    setShowDropdown,
    setDropdownElement,
    handleAsk,
    handleSearch,
  } = state;

  const classes = classNames('user-player-search-bar', className, { 'widget-mode': isWidgetMode });
  const searchSuggestionsClassname = classNames('search-suggestions', { 'widget-mode': isWidgetMode });

  useOutsideClick(dropdownElement, () => setShowDropdown(false), document.getElementById('ai-search-input'));

  const searchValue = searchQuery.value ? ellipseText(searchQuery.value, 20) : '';

  return (
    <>
      <TextInputControlled
        id="ai-search-input"
        data-tutorial-id="search-in-transcript"
        data-guided-training-id="player-training-search"
        onKeyUp={handleKeyUp}
        model={searchQuery}
        className={classes}
        aria-disabled={isAiProcessing}
        placeholder={placeholder}
        persistentFeedback={false}
        {...props} >
        {dropdownOpened && (
          <div
            ref={setDropdownElement}
            className={searchSuggestionsClassname}>
            <Suggestion 
              onClick={handleSearch} 
              text={`Search "${searchQuery.value}"`} />
            <Suggestion
              onClick={(evt) => handleAsk(evt)}
              disabled={isAiProcessing}
              text={`Ask "${searchValue}"`}
              isProcessing={isAiProcessing}
            />
            <Suggestion
              onClick={(evt) => handleAsk(evt, DEFAULT_GPT_QUESTION)}
              text={DEFAULT_GPT_QUESTION} />
          </div>
        )}
        <div 
          id="search-icon" 
          className="left-gutter flex-center lpad4">
          <div className="icons">
            <CSSTransition 
              in={!!showIcon} 
              classNames="icon-fade" 
              unmountOnExit 
              timeout={300}>
              <button 
                className="clear-search-btn" 
                onClick={handleClear}>
                <CloseSVGIcon />
              </button>
            </CSSTransition>
            <CSSTransition 
              in={!showIcon} 
              classNames="icon-fade" 
              unmountOnExit 
              timeout={300}>
              <div className="search-icon-box">
                <AiLogoSVGIcon />
              </div>
            </CSSTransition>
          </div>
        </div>
      </TextInputControlled>
    </>
  );
});
